import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create a separate axios instance for public API (no auth required)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ClinicInfo {
  id: number;
  name: string;
  timezone: string;
  language: string;
}

export interface PatientRegistrationData {
  id_number: string;  // Israeli ID number (תעודת זהות)
  first_name: string;
  last_name: string;
  date_of_birth: string;
  email: string;
  phone: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  insurance_provider?: string;
  insurance_id?: string;
  medical_history?: string;
  current_medications?: string;
  reason_for_visit?: string;
}

export interface ScheduleInviteResponse {
  has_next_meeting: boolean;
  invite_url?: string;
  message?: string;
}

// Origin of the API (e.g. https://host) used to build absolute asset URLs from API paths
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

export interface ScheduleClinicInfo {
  name: string;
  language?: string | null;
  timezone: string;
  logo_url?: string | null;
}

export interface ScheduleActiveMeeting {
  id: number;
  yoman_id: string;
  yoman_url?: string | null;
  meeting_type_id: number | null;
  meeting_type_name: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
}

export interface ScheduleNextMeeting {
  meeting_type_id: number;
  name: string;
  service_id: string;
  resource_id: string;
  duration_minutes: number;
}

export interface AvailableSlot {
  value: string;       // original Yoman (Asia/Jerusalem) string — sent back to book
  display: string;     // clinic-local time "HH:MM"
  date: string;        // clinic-local date "YYYY-MM-DD"
  resources: string[]; // candidate Yoman resource ids for this slot
}

export interface FirstMonthResponse {
  month: string | null;     // "YYYY-MM"
  from_date: string | null;
  to_date: string | null;
  slots: AvailableSlot[];   // all available slots in that month
}

export interface ScheduleContext {
  mode: 'manage' | 'book' | 'none';
  clinic: ScheduleClinicInfo;
  patient_name?: string;
  active_meeting: ScheduleActiveMeeting | null;
  next_meeting: ScheduleNextMeeting | null;
  yoman_invite_url?: string | null;
}

export interface MeetingUpdateMeeting {
  id: number;
  meeting_type_id: number | null;
  meeting_type_name: string | null;
  scheduled_at: string | null;
  duration_minutes: number | null;
  status: string | null;
}

export interface MeetingUpdateContext {
  clinic: ScheduleClinicInfo;
  patient_name?: string | null;
  reschedulable: boolean;
  meeting: MeetingUpdateMeeting;
}

export const publicService = {
  async getClinicInfo(clinicId: number): Promise<ClinicInfo> {
    const response = await publicApi.get(`/public/clinics/${clinicId}`);
    return response.data;
  },

  async registerPatient(clinicId: number, data: PatientRegistrationData): Promise<any> {
    const response = await publicApi.post(`/public/clinics/${clinicId}/register`, data);
    return response.data;
  },

  async getPatientScheduleInvite(patientUuid: string): Promise<ScheduleInviteResponse> {
    const response = await publicApi.get(`/public/patients/${patientUuid}/schedule`);
    return response.data;
  },

  async getScheduleContext(patientUuid: string): Promise<ScheduleContext> {
    const response = await publicApi.get(`/public/patients/${patientUuid}/schedule-context`);
    return response.data;
  },

  async getAvailableSlots(patientUuid: string, meetingTypeId: number, fromDate: string, toDate: string, meetingId?: number): Promise<{ slots: AvailableSlot[] }> {
    const params: Record<string, any> = { meeting_type_id: meetingTypeId, from_date: fromDate, to_date: toDate };
    if (meetingId) params.meeting_id = meetingId;
    const response = await publicApi.get(`/public/patients/${patientUuid}/available-slots`, { params });
    return response.data;
  },

  async getFirstAvailableMonth(patientUuid: string, meetingTypeId: number, meetingId?: number): Promise<FirstMonthResponse> {
    const params: Record<string, any> = { meeting_type_id: meetingTypeId };
    if (meetingId) params.meeting_id = meetingId;
    const response = await publicApi.get(`/public/patients/${patientUuid}/first-available-month`, { params });
    return response.data;
  },

  async bookMeeting(patientUuid: string, data: { meeting_type_id: number; start: string; resource_id?: string }): Promise<{ ok: boolean }> {
    const response = await publicApi.post(`/public/patients/${patientUuid}/meetings`, data);
    return response.data;
  },

  async rescheduleMeeting(patientUuid: string, meetingId: number, data: { start: string }): Promise<{ ok: boolean }> {
    const response = await publicApi.put(`/public/patients/${patientUuid}/meetings/${meetingId}`, data);
    return response.data;
  },

  async cancelMeeting(patientUuid: string, meetingId: number, data: { reason?: string }): Promise<{ ok: boolean }> {
    const response = await publicApi.post(`/public/patients/${patientUuid}/meetings/${meetingId}/cancel`, data);
    return response.data;
  },

  // ── Reschedule by meeting uuid (/update-meeting/:uuid) ───────────────────────

  async getMeetingUpdateContext(meetingUuid: string): Promise<MeetingUpdateContext> {
    const response = await publicApi.get(`/public/meetings/${meetingUuid}/update-context`);
    return response.data;
  },

  async getMeetingAvailableSlots(meetingUuid: string, fromDate: string, toDate: string): Promise<{ slots: AvailableSlot[] }> {
    const response = await publicApi.get(`/public/meetings/${meetingUuid}/available-slots`, {
      params: { from_date: fromDate, to_date: toDate },
    });
    return response.data;
  },

  async getMeetingFirstAvailableMonth(meetingUuid: string): Promise<FirstMonthResponse> {
    const response = await publicApi.get(`/public/meetings/${meetingUuid}/first-available-month`);
    return response.data;
  },

  async rescheduleMeetingByUuid(meetingUuid: string, data: { start: string }): Promise<{ ok: boolean }> {
    const response = await publicApi.put(`/public/meetings/${meetingUuid}`, data);
    return response.data;
  },

  async cancelMeetingByUuid(meetingUuid: string, data: { reason?: string }): Promise<{ ok: boolean }> {
    const response = await publicApi.post(`/public/meetings/${meetingUuid}/cancel`, data);
    return response.data;
  },
};
